import {useCallback, useEffect, useRef} from 'react'

import type {ComposerEventFields} from './composer-telemetry'
import type {ComposerAction} from './constants'
import {useComposerTelemetry} from './ComposerTelemetryContext'

export interface PickerTelemetryActions {
  opened: ComposerAction
  changed: ComposerAction
  dismissed: ComposerAction
}

/**
 * Reports a picker's opened → changed | dismissed funnel. A pick and the menu
 * closing can arrive in either order, so the dismiss check waits a tick for
 * a pending pick before deciding nothing changed.
 */
export function usePickerTelemetry(actions: PickerTelemetryActions) {
  const telemetry = useComposerTelemetry()
  const isOpenRef = useRef(false)
  const pickedRef = useRef(false)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(dismissTimerRef.current), [])
  const {opened, changed, dismissed} = actions

  const onOpenChange = useCallback(
    (open: boolean, fields: ComposerEventFields = {}) => {
      if (!telemetry || open === isOpenRef.current) return
      isOpenRef.current = open
      if (open) {
        pickedRef.current = false
        telemetry.track(opened, fields)
        return
      }
      clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = setTimeout(() => {
        dismissTimerRef.current = undefined
        if (!pickedRef.current) telemetry.track(dismissed, fields)
        pickedRef.current = false
      }, 0)
    },
    [dismissed, opened, telemetry],
  )

  /** Reports a user selection. Picking the current value counts as a dismiss, not a change. */
  const onPicked = useCallback(
    (from: string | undefined, to: string | undefined, fields: ComposerEventFields = {}) => {
      if (!telemetry || from === to) return
      pickedRef.current = true
      telemetry.track(changed, fields)
    },
    [changed, telemetry],
  )

  return {onOpenChange, onPicked}
}
